import React, { ReactElement } from "react";
import {
    makeStyles,
    Button,
    Dialog,
    DialogContent,
    IconButton,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import IDAMessageImage from "../../images/ida-message.png";
import { useEnumStrings } from "../enumStrings";

const useStyles = makeStyles(() => ({
    dialogPaper: {
        borderRadius: "20px",
        maxWidth: "560px",
        width: "560px",
        overflow: "hidden",
        "@media (max-width: 600px)": {
            borderRadius: "16px",
            maxWidth: "calc(100% - 24px)",
            width: "calc(100% - 24px)",
            margin: "12px",
        },
    },
    closeButton: {
        position: "absolute",
        top: "14px",
        right: "14px",
        zIndex: 2,
        backgroundColor: "#ED1C24",
        color: "white",
        width: "30px",
        height: "30px",
        padding: 0,
        "&:hover": {
            backgroundColor: "#D41920",
        },
    },
    idaImage: {
        display: "block",
        margin: "0 auto 8px auto",
        width: "220px",
        height: "auto",
        objectFit: "contain",
    },
    title: {
        fontFamily: "DM Sans, sans-serif",
        fontWeight: 700,
        fontSize: "18px",
        lineHeight: "1.3",
        textAlign: "center",
        color: "#000000",
        marginBottom: "8px",
        padding: "0 16px",
    },
    desc: {
        fontFamily: "DM Sans, sans-serif",
        fontSize: "14px",
        lineHeight: "1.5",
        textAlign: "center",
        color: "#24272C",
        fontWeight: 400,
        marginBottom: "18px",
        padding: "0 24px",
    },
    previewBox: {
        border: "1px solid #00B621",
        borderRadius: "8px",
        padding: "14px 14px",
        margin: "0 32px",
        maxHeight: "200px",
        overflow: "auto",
    },
    previewLabel: {
        fontFamily: "DM Sans, sans-serif",
        fontSize: "14px",
        fontWeight: 700,
        color: "#00B621",
        marginBottom: "8px",
        textAlign: "left" as const,
    },
    previewText: {
        fontFamily: "DM Sans, sans-serif",
        fontSize: "13px",
        color: "#24272C",
        lineHeight: "1.6",
        textAlign: "left" as const,
    },
    buttons: {
        display: "flex",
        justifyContent: "center",
        gap: "32px",
        margin: "24px 0 28px 0",
    },
    regenBtn: {
        backgroundColor: "#00B621",
        color: "#FFFFFF",
        fontFamily: "DM Sans, sans-serif",
        fontWeight: 700,
        fontSize: "14px",
        borderRadius: "8px",
        padding: "10px 24px",
        textTransform: "uppercase" as const,
        display: "flex",
        alignItems: "center",
        gap: "8px",
        "&:hover": { backgroundColor: "#009A1B" },
    },
    confirmBtn: {
        backgroundColor: "#E1A731",
        color: "#FFFFFF",
        fontFamily: "DM Sans, sans-serif",
        fontWeight: 700,
        fontSize: "14px",
        borderRadius: "8px",
        padding: "10px 40px",
        textTransform: "uppercase" as const,
        "&:hover": { backgroundColor: "#C99028" },
    },
}));

interface IDAOverwriteDialogProps {
    open: boolean;
    onClose: () => void;
    onRegenerate: () => void;
    onConfirm: () => void;
    title: string;
    description: string;
    generatedLabel: string;
    generatedText: string;
}

const IDAOverwriteDialog = ({
    open,
    onClose,
    onRegenerate,
    onConfirm,
    title,
    description,
    generatedLabel,
    generatedText,
}: IDAOverwriteDialogProps): ReactElement => {
    const classes = useStyles();
    const enumStrings = useEnumStrings();

    return (
        <Dialog
            open={open}
            onClose={onClose}
            classes={{ paper: classes.dialogPaper }}
        >
            <IconButton
                className={classes.closeButton}
                onClick={onClose}
            >
                <CloseIcon style={{ fontSize: "18px" }} />
            </IconButton>
            <DialogContent style={{ padding: "24px 0 0 0", textAlign: "center" }}>
                <img src={IDAMessageImage} alt="IDA" className={classes.idaImage} />
                <div className={classes.title}>{title}</div>
                <div className={classes.desc}>{description}</div>
                <div className={classes.previewBox}>
                    <div className={classes.previewLabel}>{generatedLabel}</div>
                    <div className={classes.previewText}>{generatedText}</div>
                </div>
                <div className={classes.buttons}>
                    <Button
                        className={classes.regenBtn}
                        onClick={onRegenerate}
                        variant="contained"
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg"><path d="M7.5 5.6L10 7 8.6 4.5 10 2 7.5 3.4 5 2l1.4 2.5L5 7zm12 9.8L17 14l1.4 2.5L17 19l2.5-1.4L22 19l-1.4-2.5L22 14zM22 2l-2.5 1.4L17 2l1.4 2.5L17 7l2.5-1.4L22 7l-1.4-2.5zm-7.63 5.29a.9959.9959 0 00-1.41 0L1.29 18.96c-.39.39-.39 1.02 0 1.41l2.34 2.34c.39.39 1.02.39 1.41 0L16.7 11.05c.39-.39.39-1.02 0-1.41l-2.33-2.35z" /></svg>
                        {enumStrings.idaGenerate.reGenerateButton}
                    </Button>
                    <Button
                        className={classes.confirmBtn}
                        onClick={onConfirm}
                        variant="contained"
                    >
                        {enumStrings.idaGenerate.overwriteButton}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default IDAOverwriteDialog;
